import { useState, useEffect } from "react";
import { useAdmin, ColorPalette } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Palette, Plus, Edit, Trash2, Eye, Check } from "lucide-react";
import { toast } from "sonner";
import { MATERIAL_PRESETS } from "@/lib/palettes/presets";
import { PalettePreviewModal } from "./PalettePreviewModal";

export function PaletteSelector() {
  const { getColorPalettes, setActiveColorPalette, createColorPalette, loading } = useAdmin();
  const [palettes, setPalettes] = useState<ColorPalette[]>([]);
  const [activePalette, setActivePalette] = useState<ColorPalette | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [previewPalette, setPreviewPalette] = useState<ColorPalette | null>(null);
  const [newPalette, setNewPalette] = useState({
    name: "",
    description: "",
    colors: {
      primary: "202 100% 30%",
      secondary: "202 100% 50%",
      accent: "202 100% 41%",
      sidebar: "212 100% 23%",
      success: "142 71% 45%",
      warning: "43 96% 56%",
      error: "0 84% 60%"
    }
  });

  useEffect(() => {
    fetchPalettes();
  }, []);

  const fetchPalettes = async () => {
    const data = await getColorPalettes();
    setPalettes(data);
    const active = data.find(p => p.is_active);
    setActivePalette(active || null);
  };

  const handleSetActivePalette = async (paletteId: number) => {
    const success = await setActiveColorPalette(paletteId);
    if (success) {
      fetchPalettes();
      // Apply the palette to CSS variables
      const palette = palettes.find(p => p.id === paletteId);
      if (palette) {
        applyPaletteToCSS(palette.colors);
      }
    }
  };

  const applyPaletteToCSS = (colors: Record<string, string>) => {
    const root = document.documentElement;
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
  };

  const handleCreatePalette = async () => {
    if (!newPalette.name.trim()) {
      toast.error("Nome da paleta é obrigatório");
      return;
    }

    const success = await createColorPalette(newPalette);
    if (success) {
      setNewPalette({
        name: "",
        description: "",
        colors: {
          primary: "202 100% 30%",
          secondary: "202 100% 50%",
          accent: "202 100% 41%",
          sidebar: "212 100% 23%",
          success: "142 71% 45%",
          warning: "43 96% 56%",
          error: "0 84% 60%"
        }
      });
      setIsCreating(false);
      fetchPalettes();
    }
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setNewPalette(prev => ({
      ...prev,
      colors: {
        ...prev.colors,
        [colorKey]: value
      }
    }));
  };

  const getColorPreview = (colorValue: string) => {
    return `hsl(${colorValue})`;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Gerenciamento de Paletas de Cores</span>
            <Button 
              onClick={() => setIsCreating(!isCreating)}
              variant="outline"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nova Paleta
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="existing" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="existing">Paletas Existentes</TabsTrigger>
              <TabsTrigger value="create">Criar Nova</TabsTrigger>
            </TabsList>
            
            <TabsContent value="existing" className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {palettes.map((palette) => (
                  <div 
                    key={palette.id} 
                    className={`border rounded-lg p-4 space-y-3 cursor-pointer transition-all ${
                      activePalette?.id === palette.id 
                        ? 'border-primary ring-2 ring-primary/20' 
                        : 'border-border hover:border-primary/50'
                    }`}
                    onClick={() => handleSetActivePalette(palette.id)}
                  >
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium">{palette.name}</h3>
                      <div className="flex gap-1">
                        {palette.is_default && (
                          <Badge variant="secondary" className="text-xs">Padrão</Badge>
                        )}
                        {palette.is_active && (
                          <Badge variant="default" className="text-xs">
                            <Check className="h-3 w-3 mr-1" />
                            Ativa
                          </Badge>
                        )}
                      </div>
                    </div>
                    
                    {palette.description && (
                      <p className="text-sm text-muted-foreground">{palette.description}</p>
                    )}
                    
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">Cores:</div>
                      <div className="grid grid-cols-4 gap-2">
                        {Object.entries(palette.colors).slice(0, 8).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div 
                              className="h-8 w-full rounded border border-border/50 mb-1"
                              style={{ backgroundColor: getColorPreview(value) }}
                            />
                            <div className="text-xs text-muted-foreground truncate">{key}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreviewPalette(palette);
                        }}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        Preview
                      </Button>
                      {!palette.is_default && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            // TODO: Open edit dialog
                            toast.info("Funcionalidade de edição em desenvolvimento");
                          }}
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Editar
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="create" className="space-y-4">
              <div className="space-y-4">
                {/* Preset models */}
                <div className="space-y-3">
                  <div className="text-sm font-medium">Modelos (Material)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                    {MATERIAL_PRESETS.map((preset) => (
                      <div key={preset.name} className="border rounded-lg p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-sm truncate" title={preset.name}>{preset.name}</div>
                        </div>
                        <div className="grid grid-cols-5 gap-2">
                          {Object.entries(preset.colors).slice(0,5).map(([k,v]) => (
                            <div key={k} className="h-6 rounded border border-border/50" style={{ backgroundColor: `hsl(${v})` }} />
                          ))}
                        </div>
                        <div className="text-xs text-muted-foreground line-clamp-2" title={preset.description}>{preset.description}</div>
                        <Button size="sm" className="w-full" variant="outline"
                          onClick={() => {
                            setNewPalette({
                              name: preset.name,
                              description: preset.description,
                              colors: { ...preset.colors },
                            });
                            toast.success("Modelo aplicado ao formulário");
                          }}
                        >
                          Usar modelo
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="palette-name">Nome da Paleta</Label>
                    <Input
                      id="palette-name"
                      value={newPalette.name}
                      onChange={(e) => setNewPalette(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ex: Tema Escuro"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="palette-description">Descrição</Label>
                    <Input
                      id="palette-description"
                      value={newPalette.description}
                      onChange={(e) => setNewPalette(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Ex: Tema escuro para modo noturno"
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm font-medium">Configuração de Cores</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {Object.entries(newPalette.colors).map(([key, value]) => (
                      <div key={key} className="space-y-2">
                        <Label htmlFor={`color-${key}`} className="text-sm">
                          {key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                        </Label>
                        <div className="flex gap-2">
                          <div 
                            className="h-10 w-10 rounded border border-border/50 flex-shrink-0"
                            style={{ backgroundColor: getColorPreview(value) }}
                          />
                          <Input
                            id={`color-${key}`}
                            value={value}
                            onChange={(e) => handleColorChange(key, e.target.value)}
                            placeholder="HSL values (ex: 202 100% 30%)"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={handleCreatePalette}
                    disabled={loading || !newPalette.name.trim()}
                  >
                    {loading ? "Criando..." : "Criar Paleta"}
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {previewPalette && (
        <PalettePreviewModal
          isOpen={!!previewPalette}
          onClose={() => setPreviewPalette(null)}
          palette={{
            name: previewPalette.name,
            colors: previewPalette.colors
          }}
        />
      )}
    </div>
  );
}
